import _cloneDeep from 'lodash/cloneDeep';

/* @ngInject */
export default class LabelFilterController {
    constructor($scope, $timeout, labels, onUpdateLabels, onClose, SensorService) {
        this.$scope = $scope;
        this.$timeout = $timeout;
        this.labels = labels;
        this.onUpdateLabels = onUpdateLabels;
        this.onClose = onClose;
        this.SensorService = SensorService;
    }

    $onInit() {
        // Initialize autocomplete state
        this.availableLabelKeys = [];
        this.availableLabelValues = {};
        this.filteredLabelKeys = {};
        this.filteredLabelValues = {};
        this.showKeysDropdown = {};
        this.showValuesDropdown = {};
        this.selectedKeyIndex = {};
        this.selectedValueIndex = {};
        
        // Fetch available label keys
        this.fetchLabelKeys();
    }

    fetchLabelKeys() {
        this.SensorService.listLabelKeys().then(response => {
            // Use only device label keys
            const keyMap = new Map();
            
            if (response.deviceLabelKeys) {
                response.deviceLabelKeys.forEach(item => {
                    if (keyMap.has(item.key)) {
                        keyMap.get(item.key).deviceCount += item.deviceCount;
                    } else {
                        keyMap.set(item.key, {
                            key: item.key,
                            deviceCount: item.deviceCount,
                            type: 'device'
                        });
                    }
                });
            }
            
            // Convert map to array
            this.availableLabelKeys = Array.from(keyMap.values());
            
            // Sort by device count (descending)
            this.availableLabelKeys.sort((a, b) => b.deviceCount - a.deviceCount);
        }).catch(error => {
            // eslint-disable-next-line no-console
            console.error('Error fetching label keys:', error);
            this.availableLabelKeys = [];
        });
    }

    fetchLabelValues(index) {
        const label = this.labels[index];
        if (!label.key || !label.key.trim()) {
            return;
        }

        this.SensorService.listLabelValues(label.key.trim()).then(response => {
            // Use only device label values
            const valueMap = new Map();
            
            if (response.deviceLabelValues) {
                response.deviceLabelValues.forEach(item => {
                    if (valueMap.has(item.value)) {
                        valueMap.get(item.value).deviceCount += item.deviceCount;
                    } else {
                        valueMap.set(item.value, {
                            value: item.value,
                            deviceCount: item.deviceCount,
                            type: 'device'
                        });
                    }
                });
            }
            
            // Convert map to array
            this.availableLabelValues[index] = Array.from(valueMap.values());
            
            // Sort by device count (descending)
            this.availableLabelValues[index].sort((a, b) => b.deviceCount - a.deviceCount);
            
            this.filterLabelValues(index);
        }).catch(error => {
            // eslint-disable-next-line no-console
            console.error('Error fetching label values:', error);
            this.availableLabelValues[index] = [];
            this.filteredLabelValues[index] = [];
        });
    }

    onKeyInputFocus(index) {
        this.filterLabelKeys(index);
        this.showKeysDropdown[index] = this.filteredLabelKeys[index] && this.filteredLabelKeys[index].length > 0;
    }

    onKeyInputBlur(index) {
        this.$timeout(() => {
            this.showKeysDropdown[index] = false;
        }, 200);
    }

    onKeyInputChange(index) {
        this.filterLabelKeys(index);
        this.showKeysDropdown[index] = this.filteredLabelKeys[index] && this.filteredLabelKeys[index].length > 0;
        
        // Fetch values for the new key
        if (this.labels[index].key && this.labels[index].key.trim()) {
            this.fetchLabelValues(index);
        }
    }

    filterLabelKeys(index) {
        const searchTerm = (this.labels[index].key || '').toLowerCase();
        
        if (!searchTerm) {
            this.filteredLabelKeys[index] = this.availableLabelKeys.slice(0, 50);
        } else {
            this.filteredLabelKeys[index] = this.availableLabelKeys
                .filter(item => item.key.toLowerCase().includes(searchTerm))
                .slice(0, 50);
        }
    }

    selectLabelKey(index, key) {
        this.labels[index].key = key;
        this.showKeysDropdown[index] = false;
        this.fetchLabelValues(index);
    }

    onValueInputFocus(index) {
        if (!this.availableLabelValues[index]) {
            this.fetchLabelValues(index);
        }
        this.filterLabelValues(index);
        this.showValuesDropdown[index] = this.filteredLabelValues[index] && this.filteredLabelValues[index].length > 0;
    }

    onValueInputBlur(index) {
        this.$timeout(() => {
            this.showValuesDropdown[index] = false;
        }, 200);
    }

    onValueInputChange(index) {
        this.filterLabelValues(index);
        this.showValuesDropdown[index] = this.filteredLabelValues[index] && this.filteredLabelValues[index].length > 0;
    }

    filterLabelValues(index) {
        if (!this.availableLabelValues[index]) {
            this.filteredLabelValues[index] = [];
            return;
        }

        const searchTerm = (this.labels[index].value || '').toLowerCase();
        
        if (!searchTerm) {
            this.filteredLabelValues[index] = this.availableLabelValues[index].slice(0, 50);
        } else {
            this.filteredLabelValues[index] = this.availableLabelValues[index]
                .filter(item => item.value.toLowerCase().includes(searchTerm))
                .slice(0, 50);
        }
    }

    selectLabelValue(index, value) {
        this.labels[index].value = value;
        this.showValuesDropdown[index] = false;
    }

    getCountDisplayText(count) {
        if (!count) return '';
        return count === 1 ? '1 device' : `${count} devices`;
    }

    close() {
        this.onClose();
    }

    apply() {
        // Pass the updated labels back to the parent and close
        this.onUpdateLabels(_cloneDeep(this.labels));
        this.close();
    }

    clear() {
        // Clear all label filters
        this.labels.splice(0, this.labels.length);
        // Add one empty label row
        this.labels.push({ key: '', value: '' });
        this.apply();
    }

    addLabel() {
        // Add a new empty label row (max 10)
        if (this.labels.length < 10) {
            this.labels.push({ key: '', value: '' });
        }
    }

    removeLabel(index) {
        // Remove label at index
        if (this.labels.length > 1) {
            this.labels.splice(index, 1);
        } else {
            // If it's the last one, just clear it
            this.labels[0] = { key: '', value: '' };
        }
    }
    
    canAddMoreLabels() {
        return this.labels.length < 10;
    }
}

